import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AmfTrustBadge } from '@/components/ui/amf-trust-badge';
import { MagneticButton } from '@/components/ui/magnetic-button';
import { useLang } from '@/lib/LanguageContext';
import { ta } from '@/lib/i18n';
import { t } from '@/lib/translations';
import { trackLead } from '@/lib/analytics/meta-pixel';

// Phase 27.89 — formulaire lead EXTRAIT (réutilisable) : utilisé inline dans la
// section #contact ET dans le popup modal ouvert par les CTA. Une seule source.
// useId() garantit des IDs uniques par instance (section + modal coexistent).

// Lecture d'un cookie (client only). _fbp/_fbc sont posés par le pixel Meta
// (donc présents uniquement si consentement marketing donné) — on les relaie au
// worker pour améliorer la qualité de matching du CAPI serveur.
function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : undefined;
}

const AGE_RANGE_VALUES = ['', '25-35', '35-45', '45-55', '55 et +'] as const;
const FAMILY_STATUS_VALUES = [
  '',
  'famille-enfants',
  'couple-sans-enfants',
  'monoparental',
  'celibataire'
] as const;

const Schema = z.object({
  first_name: z.string().min(1, 'required').max(50),
  last_name: z.string().min(1, 'required').max(50),
  email: z.string().email('email_invalid').max(100),
  phone: z.string().min(10, 'phone_short').max(20),
  age_range: z.enum(AGE_RANGE_VALUES).optional().default(''),
  family_status: z.enum(FAMILY_STATUS_VALUES).refine((v) => v !== '', { message: 'required' }),
  message: z.string().max(1000).optional().or(z.literal('')),
  consent_loi25: z.literal('on', { message: 'consent_required' })
});

type FormText = {
  required_field: string;
  error_email_invalid: string;
  error_phone_short: string;
  error_consent_required: string;
};

function localizeError(code: string, text: FormText): string {
  switch (code) {
    case 'required':
      return text.required_field;
    case 'email_invalid':
      return text.error_email_invalid;
    case 'phone_short':
      return text.error_phone_short;
    case 'consent_required':
      return text.error_consent_required;
    default:
      return text.required_field;
  }
}

export function LeadForm({ onSuccess }: { onSuccess: () => void }) {
  const { lang } = useLang();
  const text = ta(t.form, lang);
  const uid = useId();
  const fid = (s: string) => `${uid}-${s}`;
  const mountTimeRef = useRef<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    mountTimeRef.current = Date.now();
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    // AJAX : on intercepte toujours. Fallback no-JS = le form natif (action=/api/lead)
    // redirige vers /merci quand JS est désactivé.
    event.preventDefault();
    const formEl = event.currentTarget;
    const fd = new FormData(formEl);
    fd.set('elapsed_ms', String(Date.now() - mountTimeRef.current));

    const result = Schema.safeParse({
      first_name: fd.get('first_name'),
      last_name: fd.get('last_name'),
      email: fd.get('email'),
      phone: fd.get('phone'),
      age_range: fd.get('age_range') ?? '',
      family_status: fd.get('family_status') ?? '',
      message: fd.get('message') ?? '',
      consent_loi25: fd.get('consent_loi25')
    });

    if (!result.success) {
      const nextErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0]?.toString() ?? 'form';
        nextErrors[key] = localizeError(issue.message, text);
      }
      setErrors(nextErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);

    // event_id partagé pixel navigateur <-> CAPI serveur (worker) → dédup Meta.
    // Généré ici, envoyé au worker dans le body ET passé à trackLead() : même ID
    // des 2 côtés = Meta fusionne en 1 Lead (sinon doublon sur les accepteurs).
    const eventId = crypto.randomUUID();
    fd.set('event_id', eventId);
    const fbp = readCookie('_fbp');
    if (fbp) fd.set('fbp', fbp);
    const fbc = readCookie('_fbc');
    if (fbc) fd.set('fbc', fbc);

    try {
      const res = await fetch('/api/lead', { method: 'POST', body: fd });
      if (res.ok && !res.url.includes('error=')) {
        setSubmitting(false);
        formEl.reset();
        trackLead(eventId); // conversion Meta « Lead » (consent-gated, no-op sinon)
        onSuccess();
      } else {
        setSubmitting(false);
        setErrors({ form: text.error });
      }
    } catch {
      setSubmitting(false);
      setErrors({ form: text.error });
    }
  };

  return (
    <form
      action="/api/lead"
      method="POST"
      encType="multipart/form-data"
      onSubmit={handleSubmit}
      noValidate
      className="space-y-6"
    >
      {/* Honeypot — hidden from real users + 1Password-resistant name */}
      <input
        type="text"
        name="contact_check_url"
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="new-password"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
        defaultValue=""
      />

      {/* Hidden timing — populated at submit time */}
      <input type="hidden" name="elapsed_ms" defaultValue="0" />

      {/* first_name */}
      <div>
        <label htmlFor={fid('first-name')} className="mb-2 block font-sans text-xs font-bold uppercase tracking-widest text-foreground">
          {text.label_first_name} *
        </label>
        <Input
          id={fid('first-name')}
          name="first_name"
          type="text"
          required
          maxLength={50}
          aria-invalid={errors.first_name ? true : undefined}
          aria-describedby={errors.first_name ? fid('first-name-error') : undefined}
          className={errors.first_name ? 'border-primary' : undefined}
        />
        {errors.first_name && (
          <p id={fid('first-name-error')} role="alert" className="mt-1 text-sm text-primary">{errors.first_name}</p>
        )}
      </div>

      {/* last_name */}
      <div>
        <label htmlFor={fid('last-name')} className="mb-2 block font-sans text-xs font-bold uppercase tracking-widest text-foreground">
          {text.label_last_name} *
        </label>
        <Input
          id={fid('last-name')}
          name="last_name"
          type="text"
          required
          maxLength={50}
          aria-invalid={errors.last_name ? true : undefined}
          aria-describedby={errors.last_name ? fid('last-name-error') : undefined}
          className={errors.last_name ? 'border-primary' : undefined}
        />
        {errors.last_name && (
          <p id={fid('last-name-error')} role="alert" className="mt-1 text-sm text-primary">{errors.last_name}</p>
        )}
      </div>

      {/* email */}
      <div>
        <label htmlFor={fid('email')} className="mb-2 block font-sans text-xs font-bold uppercase tracking-widest text-foreground">
          {text.label_email} *
        </label>
        <Input
          id={fid('email')}
          name="email"
          type="email"
          required
          maxLength={100}
          aria-invalid={errors.email ? true : undefined}
          aria-describedby={errors.email ? fid('email-error') : undefined}
          className={errors.email ? 'border-primary' : undefined}
        />
        {errors.email && (
          <p id={fid('email-error')} role="alert" className="mt-1 text-sm text-primary">{errors.email}</p>
        )}
      </div>

      {/* phone */}
      <div>
        <label htmlFor={fid('phone')} className="mb-2 block font-sans text-xs font-bold uppercase tracking-widest text-foreground">
          {text.label_phone} *
        </label>
        <Input
          id={fid('phone')}
          name="phone"
          type="tel"
          required
          maxLength={20}
          aria-invalid={errors.phone ? true : undefined}
          aria-describedby={errors.phone ? fid('phone-error') : undefined}
          className={errors.phone ? 'border-primary' : undefined}
        />
        {errors.phone && (
          <p id={fid('phone-error')} role="alert" className="mt-1 text-sm text-primary">{errors.phone}</p>
        )}
      </div>

      {/* age_range — optionnel */}
      <div>
        <label htmlFor={fid('age-range')} className="mb-2 block font-sans text-xs font-bold uppercase tracking-widest text-foreground">
          {text.label_age_range}
        </label>
        <select
          id={fid('age-range')}
          name="age_range"
          defaultValue=""
          className="w-full border-0 border-b border-border bg-transparent px-1 py-2 font-sans text-foreground transition-colors focus:border-primary focus:outline-none focus:ring-0 disabled:opacity-50"
        >
          <option value="">{text.select_default}</option>
          <option value="25-35">{text.age_25_35}</option>
          <option value="35-45">{text.age_35_45}</option>
          <option value="45-55">{text.age_45_55}</option>
          <option value="55 et +">{text.age_55_65}</option>
        </select>
      </div>

      {/* family_status — OBLIGATOIRE (moteur scoring) */}
      <div>
        <label htmlFor={fid('family-status')} className="mb-2 block font-sans text-xs font-bold uppercase tracking-widest text-foreground">
          {text.label_family_status} *
        </label>
        <select
          id={fid('family-status')}
          name="family_status"
          defaultValue=""
          required
          aria-invalid={errors.family_status ? true : undefined}
          aria-describedby={errors.family_status ? fid('family-status-error') : undefined}
          className={`w-full border-0 border-b bg-transparent px-1 py-2 font-sans text-foreground transition-colors focus:border-primary focus:outline-none focus:ring-0 disabled:opacity-50 ${errors.family_status ? 'border-primary' : 'border-border'}`}
        >
          <option value="">{text.select_default}</option>
          <option value="famille-enfants">{text.family_with_children}</option>
          <option value="couple-sans-enfants">{text.couple_no_children}</option>
          <option value="monoparental">{text.family_single_parent}</option>
          <option value="celibataire">{text.family_single}</option>
        </select>
        {errors.family_status && (
          <p id={fid('family-status-error')} role="alert" className="mt-1 text-sm text-primary">{errors.family_status}</p>
        )}
      </div>

      {/* message */}
      <div>
        <label htmlFor={fid('message')} className="mb-2 block font-sans text-xs font-bold uppercase tracking-widest text-foreground">
          {text.label_message}
        </label>
        <textarea
          id={fid('message')}
          name="message"
          maxLength={1000}
          rows={4}
          placeholder={text.placeholder_message}
          className="w-full border-0 border-b border-border bg-transparent px-1 py-2 font-sans text-foreground placeholder:text-muted-foreground transition-colors focus:border-primary focus:outline-none focus:ring-0 disabled:opacity-50"
        />
        {errors.message && <p className="mt-1 text-sm text-primary">{errors.message}</p>}
      </div>

      {/* consent_loi25 */}
      <div className="flex items-start gap-3">
        <input
          id={fid('consent')}
          type="checkbox"
          name="consent_loi25"
          required
          aria-invalid={errors.consent_loi25 ? true : undefined}
          aria-describedby={errors.consent_loi25 ? fid('consent-error') : undefined}
          className="mt-1 h-4 w-4 cursor-pointer accent-primary"
        />
        <label htmlFor={fid('consent')} className="cursor-pointer font-sans text-sm leading-relaxed text-muted-foreground">
          {text.consent_loi25} *
        </label>
      </div>
      {errors.consent_loi25 && (
        <p id={fid('consent-error')} role="alert" className="text-sm text-primary">{errors.consent_loi25}</p>
      )}

      {/* Submit */}
      <MagneticButton className="block w-full">
        <Button type="submit" disabled={submitting} data-cursor="cta" className="border-beam w-full">
          {submitting ? text.submitting : text.submit}
        </Button>
      </MagneticButton>

      {/* Erreur générique (réseau/service) */}
      {errors.form && (
        <p role="alert" className="text-center text-sm text-primary">{errors.form}</p>
      )}

      {/* Trust signals sous submit */}
      <div className="mt-4 flex flex-col items-center gap-3">
        <p className="font-sans text-sm text-muted-foreground">{ta(t.trust, lang).microcopy_contact}</p>
        <AmfTrustBadge />
      </div>
    </form>
  );
}
